import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  LinearProgress,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  PictureAsPdf as PdfIcon,
  ListAlt as DetailIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import bacService, { BacSujet, BacSujetDetail, CouvertureResult, RentabiliteRow } from '../../services/bac.service';
import skillSetService, { SkillSet } from '../../services/skillSet.service';
import studentService from '../../services/student.service';

type Mode = 'sujets' | 'couverture' | 'rentabilite';

const Bac: React.FC = () => {
  const [mode, setMode] = useState<Mode>('sujets');
  const [sujets, setSujets] = useState<BacSujet[]>([]);
  const [loading, setLoading] = useState(true);
  const [annee, setAnnee] = useState<number | 0>(0);

  const [detail, setDetail] = useState<BacSujetDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [skillSets, setSkillSets] = useState<SkillSet[]>([]);
  const [students, setStudents] = useState<any[]>([]);
  const [skillSetId, setSkillSetId] = useState<number | ''>('');
  const [studentId, setStudentId] = useState<number | ''>('');
  const [couverture, setCouverture] = useState<CouvertureResult | null>(null);
  const [couvLoading, setCouvLoading] = useState(false);

  const [rentabilite, setRentabilite] = useState<RentabiliteRow[] | null>(null);
  const [rentLoading, setRentLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setSujets(await bacService.getAll());
      } catch (e) {
        console.error(e);
        toast.error('Erreur lors du chargement des sujets de bac');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (mode !== 'couverture' || skillSets.length) return;
    (async () => {
      try {
        const [sets, studs] = await Promise.all([skillSetService.getAll(), studentService.getAll()]);
        setSkillSets(sets);
        setStudents(studs as any[]);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [mode, skillSets.length]);

  useEffect(() => {
    if (mode !== 'rentabilite') return;
    (async () => {
      try {
        setRentLoading(true);
        const data = await bacService.rentabilite(annee || undefined);
        setRentabilite(data.skills);
      } catch (e) {
        console.error(e);
        toast.error('Erreur lors du calcul de la rentabilité');
      } finally {
        setRentLoading(false);
      }
    })();
  }, [mode, annee]);

  const annees = useMemo(() => Array.from(new Set(sujets.map(s => s.annee))).sort((a, b) => b - a), [sujets]);
  const visibles = useMemo(() => (annee ? sujets.filter(s => s.annee === annee) : sujets), [sujets, annee]);

  const openDetail = async (id: number) => {
    try {
      setDetailLoading(true);
      setDetail(await bacService.get(id));
    } catch (e) {
      console.error(e);
      toast.error('Erreur lors du chargement du sujet');
    } finally {
      setDetailLoading(false);
    }
  };

  const runCouverture = async () => {
    if (!skillSetId && !studentId) {
      toast.error('Choisissez un programme ou un élève');
      return;
    }
    try {
      setCouvLoading(true);
      const res = await bacService.couverture({
        skill_set: skillSetId || undefined,
        student: studentId || undefined,
        annee: annee || undefined,
        prochains: 15,
      });
      setCouverture(res);
    } catch (e) {
      console.error(e);
      toast.error('Erreur lors du calcul de la couverture');
    } finally {
      setCouvLoading(false);
    }
  };

  const pct = (x: number) => `${Math.round(x * 100)} %`;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5">Sujets de bac</Typography>
        <Box display="flex" gap={1} alignItems="center">
          <TextField select size="small" label="Année" value={annee} onChange={e => setAnnee(Number(e.target.value))} sx={{ minWidth: 120 }}>
            <MenuItem value={0}>Toutes</MenuItem>
            {annees.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
          </TextField>
          <ToggleButtonGroup size="small" exclusive value={mode} onChange={(_, v) => v && setMode(v)}>
            <ToggleButton value="sujets">Sujets</ToggleButton>
            <ToggleButton value="couverture">Couverture élève</ToggleButton>
            <ToggleButton value="rentabilite">Rentabilité des SF</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {mode === 'sujets' && (
        visibles.length === 0 ? (
          <Typography color="text.secondary" fontStyle="italic">
            Aucun sujet importé. Lancez la commande import_bac_mapping sur le serveur.
          </Typography>
        ) : (
          <Grid container spacing={2}>
            {visibles.map(s => (
              <Grid item xs={12} sm={6} md={4} key={s.id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontSize: '1rem' }}>{s.title}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {s.nb_questions} questions, {s.nb_skills} savoir-faire, barème {s.bareme_source === 'estime' ? 'estimé' : s.bareme_source}
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      {s.themes.map(t => (
                        <Typography key={t.num} variant="body2" color="text.secondary">
                          Ex {t.num} ({Number(t.points)} pts) : {t.theme}
                        </Typography>
                      ))}
                    </Box>
                  </CardContent>
                  <CardActions sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                    {s.sujet_pdf_url && (
                      <Button size="small" startIcon={<PdfIcon />} href={s.sujet_pdf_url} target="_blank" rel="noopener">Sujet</Button>
                    )}
                    {s.corrige_pdf_url && (
                      <Button size="small" startIcon={<PdfIcon />} href={s.corrige_pdf_url} target="_blank" rel="noopener">Corrigé neo</Button>
                    )}
                    {s.corrige_apmep_pdf_url && (
                      <Button size="small" startIcon={<PdfIcon />} href={s.corrige_apmep_pdf_url} target="_blank" rel="noopener">APMEP</Button>
                    )}
                    <Button size="small" startIcon={<DetailIcon />} onClick={() => openDetail(s.id)}>Questions</Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )
      )}

      {mode === 'couverture' && (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Part de chaque sujet qu'un élève sait faire : une question est réalisable si tous ses savoir-faire sont maîtrisés.
            Les savoir-faire d'un élève viennent de ses évaluations d'exercices (savoir-faire bien exécutés).
          </Typography>
          <Box display="flex" gap={1} flexWrap="wrap" alignItems="center" sx={{ mb: 2 }}>
            <TextField select size="small" label="Programme" value={skillSetId} onChange={e => setSkillSetId(e.target.value === '' ? '' : Number(e.target.value))} sx={{ minWidth: 220 }}>
              <MenuItem value="">Aucun</MenuItem>
              {skillSets.map(ss => <MenuItem key={ss.id} value={ss.id}>{ss.title} ({ss.skills_count} SF)</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Élève" value={studentId} onChange={e => setStudentId(e.target.value === '' ? '' : Number(e.target.value))} sx={{ minWidth: 220 }}>
              <MenuItem value="">Aucun</MenuItem>
              {students.map(st => (
                <MenuItem key={st.id} value={st.id}>{st.first_name} {st.last_name}</MenuItem>
              ))}
            </TextField>
            <Button variant="contained" onClick={runCouverture} disabled={couvLoading}>Calculer</Button>
            {couvLoading && <CircularProgress size={20} />}
          </Box>

          {couverture && (
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                {couverture.mastered_count} savoir-faire maîtrisés : {pct(couverture.ensemble.part)} de l'ensemble
                ({couverture.ensemble.points} / {couverture.ensemble.total} points)
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Sujet</TableCell>
                    <TableCell align="right">Points</TableCell>
                    <TableCell align="right">Questions</TableCell>
                    <TableCell sx={{ width: '35%' }}>Part</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {couverture.sujets.map(r => (
                    <TableRow key={r.id}>
                      <TableCell>{r.title}</TableCell>
                      <TableCell align="right">{r.points} / {r.total}</TableCell>
                      <TableCell align="right">{r.questions_ok} / {r.questions_total}</TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <LinearProgress variant="determinate" value={r.part * 100} sx={{ flex: 1, height: 8, borderRadius: 4 }} />
                          <Typography variant="body2" sx={{ minWidth: 40 }}>{pct(r.part)}</Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {couverture.prochains.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle1">Prochains savoir-faire à apprendre (gain en points sur l'ensemble)</Typography>
                  {couverture.prochains.map(p => (
                    <Typography key={p.id} variant="body2">+{p.gain} pts : [{p.id}] {p.name}</Typography>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}

      {mode === 'rentabilite' && (
        rentLoading || !rentabilite ? (
          <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>
        ) : (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Points de bac apportés par chaque savoir-faire. « Partagés » : les points de la question sont répartis entre ses savoir-faire au prorata des poids. « Pleins » : chaque savoir-faire de la question reçoit tous ses points.
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Savoir-faire</TableCell>
                  <TableCell>Contenant</TableCell>
                  <TableCell align="right">Pts partagés</TableCell>
                  <TableCell align="right">/ sujet</TableCell>
                  <TableCell align="right">Pts pleins</TableCell>
                  <TableCell align="right">Questions</TableCell>
                  <TableCell align="right">Sujets</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rentabilite.map((r, i) => (
                  <TableRow key={r.id}>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell>[{r.id}] {r.name}</TableCell>
                    <TableCell>{r.container || '-'}</TableCell>
                    <TableCell align="right">{r.points_partage}</TableCell>
                    <TableCell align="right">{r.points_partage_par_sujet}</TableCell>
                    <TableCell align="right">{r.points_plein}</TableCell>
                    <TableCell align="right">{r.nb_questions}</TableCell>
                    <TableCell align="right">{r.nb_sujets}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )
      )}

      <Dialog open={!!detail || detailLoading} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
        <DialogTitle>{detail ? detail.title : 'Chargement…'}</DialogTitle>
        <DialogContent dividers>
          {detailLoading && <Box display="flex" justifyContent="center" p={2}><CircularProgress /></Box>}
          {detail && detail.exercices.map(ex => (
            <Box key={ex.id} sx={{ mb: 2 }}>
              <Typography variant="subtitle1">Exercice {ex.num} ({Number(ex.points)} pts) : {ex.theme}</Typography>
              {ex.questions.map(q => (
                <Box key={q.id} sx={{ pl: 1, py: 0.5, borderLeft: '3px solid', borderColor: 'divider', mb: 0.5 }}>
                  <Typography variant="body2">
                    <strong>{q.ref}</strong> <span style={{ color: 'gray' }}>({Number(q.points)} pt)</span> {q.enonce_court}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                    {q.skills.map(sk => (
                      <Chip key={sk.id} size="small" variant={sk.weight >= 1 ? 'filled' : 'outlined'} label={`[${sk.id}] ${sk.name}`} />
                    ))}
                    {q.skills.length === 0 && <Chip size="small" color="warning" label="aucun savoir-faire" />}
                  </Box>
                </Box>
              ))}
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>Fermer</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Bac;
